/**
 * ══ MA TRẬN MENU × VAI — cấu trúc chép nguyên `proship-trans/src/components/Layout.tsx` NAV_AZ ══
 *
 * ⭐ TỆP NÀY KHÔNG CHỨA MỘT MẢNH LOGIC PHÂN QUYỀN NÀO (quyết định P13, chốt 05/09).
 *
 * Bản cũ khai quyền NGAY TRONG bảng menu (`roles: OFFICE`, `extraOk: azCanSeeCost`) rồi `routeAccess.ts`
 * khai LẠI một bảng thứ hai cho chốt chặn đường dẫn — hai bảng, hai nguồn, giữ khớp nhau BẰNG TRÍ NHỚ
 * ("Bảng này phải KHỚP với …, sửa một bên thì sửa cả hai"). Task 18 mất 3 vòng review vì đúng họ lỗi
 * đó: chép một mệnh đề phân quyền CÓ ĐIỀU KIỆN (`extraOk: azCanSeeCost`) thành một danh sách vai
 * TĨNH, làm mất nhánh chỉ lộ ra khi dữ liệu TỪNG NGƯỜI khác mặc định.
 *
 * Nay: `visibleLeaves()` gọi THẲNG `canShowInMenu()` của `@rs/policy/routes` (= `canAccessRoute()` cộng
 * `menuRoles` HẸP hơn nếu khai — ca duy nhất `/az/pricing`, C5 17/10) — MỘT nguồn sự thật cho
 * cả menu lẫn chốt chặn máy chủ, không thể lệch nữa. `Leaf` vì thế CỐ Ý KHÔNG có trường `roles` và
 * KHÔNG có `extraOk`: giữ lại là mời người sau sửa một bên quên bên kia.
 * Đã kiểm bằng máy trước khi gộp: **cả 13 lá `NAV_AZ` và `/settings` của `NAV_BOTTOM` đều có mặt
 * trong `RESTRICTED_ROUTES`** ⇒ phép gộp phủ kín, không lá nào rơi vào nhánh "không khai báo ⇒ ai
 * đăng nhập cũng vào được" của `canAccessRoute`.
 *
 * ⚠ CHỈ import cửa `@rs/policy/routes` (dữ liệu thuần). Cửa mặc định `@rs/policy` kéo cedar-wasm +
 *   `node:fs` ⇒ vỡ lúc dựng `apps/web` và phá cổng ngân sách 200 KB gzip. `.dependency-cruiser.cjs`
 *   luật `web-khong-dung-cua-cedar-cua-policy` là MÁY CHẶN cho đúng bất biến này, không phải chú thích.
 *
 * THỨ TỰ MỤC LÀ MỘT PHẦN CỦA ĐẶC TẢ. Đảo mục là P2, thiếu mục là P1 — xem `toBlocks()` bên dưới.
 */
import { canShowInMenu, type CostViewFlags } from '@rs/policy/routes'

export interface Leaf {
  to: string
  /** Khoá i18n trong namespace `nav` (chép đúng khoá bản cũ — `i18n.ts` mục `nav`). */
  key: string
  /** Tiêu đề THƯ MỤC CON trong nhóm. Không có = mục đứng độc lập. */
  section?: string
}

export interface NavGroup {
  key: string
  children: Leaf[]
}

/**
 * LOGISTICS RT — chép ĐÚNG thứ tự khai báo của `Layout.tsx:133-166`.
 * Chú thích lý do từng mục giữ nguyên của bản cũ, KHÔNG diễn giải lại.
 */
export const NAV_AZ: NavGroup = {
  key: 'az',
  children: [
    // Dashboard CEO/KTT/BGĐ (user 18/08: "nằm ngoài Bán hàng, phía trên") — KHÔNG có `section` nên
    // đứng ĐỘC LẬP ngay đầu menu, giống Duyệt chi/Kế toán/Danh mục đứng riêng phía dưới.
    { to: '/az/dashboard', key: 'azDashboard' },
    // ── Thư mục con BÁN HÀNG (user chốt 15/07): Đơn hàng · Chứng từ BH · Giá công bố · Thống kê ──
    { to: '/az/sales', key: 'azSales', section: 'azSalesGrp' },
    { to: '/az/billing', key: 'azBilling', section: 'azSalesGrp' },
    { to: '/az/pricing', key: 'azPricing', section: 'azSalesGrp' },
    { to: '/az/stats', key: 'azStats', section: 'azSalesGrp' },
    // ── Thư mục con VẬN HÀNH ──
    { to: '/az/ops', key: 'azOps', section: 'azOpsGrp' },
    // Điều hành vỏ lạnh (user chốt 19/08): 1 MỤC MENU DUY NHẤT, các khối con nằm trong màn Shell.
    { to: '/az/container-ops', key: 'azCoOrders', section: 'azOpsGrp' },
    // 2 MÀN THUẦN GIÁ VỐN — bản cũ `roles: OFFICE, extraOk: azCanSeeCost` (user chốt 05/08: mở
    // riêng TỪNG NGƯỜI bằng tick `az_see_vendor_cost`, không mở cả vai). Điều kiện đó nay nằm
    // TRỌN trong `canAccessRoute` (`costViewEscalation`) — xem C10.
    { to: '/az/purchase-docs', key: 'azPurchaseDocs', section: 'azOpsGrp' },
    { to: '/az/vendor-pnl', key: 'azVendorPnl', section: 'azOpsGrp' },
    /* ⭐ F25 Giá đường sắt · F28 Chào giá — ĐÃ RỜI KHỎI ĐÂY (anh chốt 24/09/2026), xem
       `RS_ONLY_LEAVES` ngay dưới. */
    // Danh mục vận hành — user 19/07 SIẾT: chỉ Trưởng phòng KD · KTT · BGĐ · admin.
    { to: '/az/ops-catalog', key: 'azOpsCatalog', section: 'azOpsGrp' },
    /* ⭐ BA MỤC F27/`0126`/F31 — Hoa hồng · Hoa hồng của tôi · Kế hoạch — ĐÃ RỜI KHỎI ĐÂY,
       xem `RS_ONLY_LEAVES` ngay dưới. */
    { to: '/approvals', key: 'azApprovals' },
    /* ⭐ F19 Chi tiền — ĐÃ RỜI KHỎI ĐÂY (anh chốt 24/09/2026), xem `RS_ONLY_LEAVES` ngay dưới. */
    // AR · AP · Sổ phụ & dòng tiền · Chứng từ AMIS GỘP 1 MỤC "Kế toán" → /quan-tri (user 18/08).
    { to: '/quan-tri', key: 'quanTri' },
    { to: '/catalog', key: 'azCatalog' },
  ],
}

/**
 * ══ MÀN CỦA RIÊNG rs-erp — GIỮ ĐƯỜNG DẪN, BỎ MỤC MENU (anh chốt 17/09/2026) ═══════════════════
 *
 * `Layout.tsx` NAV_AZ của bản cũ KHÔNG có các mục này: đứng ở LOGISTICS RT bên proship thì sidebar
 * đi thẳng từ thư mục Vận hành sang Duyệt chi. Các màn dưới đây là thứ rs-erp dựng thêm, nên để
 * trong menu là một chỗ lệch NHÌN THẤY NGAY khi mở hai màn cạnh nhau.
 *
 * ⚠ CHỈ ẨN MỤC MENU. Mã màn, đường dẫn và luật vai trong `RESTRICTED_ROUTES` GIỮ NGUYÊN — gõ thẳng
 *   `/az/hoa-hong`, `/hoa-hong-cua-toi`, `/az/ke-hoach` vẫn vào được đúng như trước. Muốn hiện lại:
 *   chuyển mảng này trở vào `NAV_AZ.children` ĐÚNG CHỖ CŨ (ngay trước `/approvals`) — thứ tự mục là
 *   một phần của đặc tả, xem chú thích đầu tệp.
 *
 * ⚠ Ẩn `/hoa-hong-cua-toi` đắt nhất: đó là màn DUY NHẤT vai `sale` xem được tiền hoa hồng của
 *   chính mình, và không còn mục menu thì họ phải được cho đường dẫn mới vào được.
 */
export const RS_ONLY_LEAVES: Leaf[] = [
  /* 24/09/2026 — thêm hai mục của thư mục VẬN HÀNH (bản cũ cũng không có). Muốn hiện lại: trả vào
     `NAV_AZ.children` ngay sau `/az/vendor-pnl`, GIỮ `section: 'azOpsGrp'`. */
  { to: '/az/gia-duong-sat', key: 'azRailPrice', section: 'azOpsGrp' },
  { to: '/az/chao-gia', key: 'azBidding', section: 'azOpsGrp' },
  /* 24/09/2026 — F19 Chi tiền (trước 19/09 là tab thứ hai của `/approvals`, rồi tách thành mục riêng).
     Muốn hiện lại: trả vào `NAV_AZ.children` ngay sau `/approvals`. */
  { to: '/chi', key: 'azDisburse' },
  { to: '/az/hoa-hong', key: 'azCommission' },
  { to: '/hoa-hong-cua-toi', key: 'myCommission' },
  { to: '/az/ke-hoach', key: 'azPlan' },
]

/** Đáy sidebar — `Layout.tsx:176-181` (trong phạm vi RT chỉ còn Cài đặt, 3 mục kia đã ở NAV_AZ). */
export const NAV_BOTTOM: Leaf[] = [{ to: '/settings', key: 'settings' }]

/**
 * Mục menu vai này NHÌN THẤY.
 *
 * ⚠ `u` (cờ giá vốn TỪNG NGƯỜI) PHẢI được truyền xuống từ người đang đăng nhập — C10, "Nợ mang
 * sang Task 19". Bỏ nó đi thì một `sale` được tick `az_see_vendor_cost` bị ẨN OAN hai mục
 * `/az/purchase-docs` + `/az/vendor-pnl` trong khi MÁY CHỦ vẫn cho vào ⇒ menu và chốt chặn lệch nhau.
 */
export const visibleLeaves = (role: string, u: CostViewFlags = {}): Leaf[] =>
  NAV_AZ.children.filter((c) => canShowInMenu(role, c.to, u))

/** Như trên, cho khối đáy sidebar. Dùng CHUNG `canShowInMenu` — không có bảng vai thứ hai. */
export const visibleBottom = (role: string, u: CostViewFlags = {}): Leaf[] =>
  NAV_BOTTOM.filter((c) => canShowInMenu(role, c.to, u))

export type Block = { section: string; items: Leaf[] } | { leaf: Leaf }

/**
 * ══ CHÉP NGUYÊN `Layout.tsx:288-300` — ĐỌC CHÚ THÍCH GỐC TRƯỚC KHI SỬA ══════════════════════════
 * *"Cắt danh sách con thành các KHỐI: khối có section (thư mục con, sổ xuống được) và leaf lẻ.
 *   Giữ nguyên thứ tự khai báo trong NAV — KHÔNG SORT, KHÔNG GOM LẠI."*
 *
 * ⚠ P14 (phát hiện 05/09): kế hoạch Task 19 dựng khối bằng
 * `[...new Set(leaves.map(l => l.section ?? ''))]` rồi render nhánh `''` bằng
 * `leaves.filter(l => !l.section)`. Vì `/az/dashboard` (mục lẻ) đứng ĐẦU, `''` vào Set trước ⇒ TẤT
 * CẢ mục lẻ bị gom và render tại vị trí đó — kéo `/approvals` · `/quan-tri` · `/catalog` từ CUỐI
 * menu lên NGAY SAU Dashboard. Thuật toán dưới đây duyệt TUẦN TỰ: mục lẻ đẩy ra `{leaf}` TẠI CHỖ;
 * mục có `section` gộp vào khối trước đó CHỈ KHI liền kề và cùng section.
 */
export const toBlocks = (children: Leaf[]): Block[] => {
  const out: Block[] = []
  for (const c of children) {
    if (!c.section) {
      out.push({ leaf: c })
      continue
    }
    const last = out[out.length - 1]
    if (last && 'section' in last && last.section === c.section) last.items.push(c)
    else out.push({ section: c.section, items: [c] })
  }
  return out
}
